import React, { useEffect, useState } from "react";
import {
  Text,
  Stack,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Input,
  Button,
  Textarea,
  Select,
  Tabs,
  TabList,
  Tab,
  Box,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Spinner,
  Center,
  VStack,
  HStack,
  Divider,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import BrandButton from "../brandButton/BrandButton";
import { useMutation, mutateFunction } from "../../libs/apis";
import { CustomToast } from "../toast/CustomToast";
import { AiOutlineFileAdd } from "react-icons/ai";
import { useData } from "../../data";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { CheckIcon, CloseIcon, WarningIcon } from "@chakra-ui/icons";
import { FaUser, FaFileAlt, FaDollarSign, FaVideo, FaBuilding } from "react-icons/fa";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export default function PitchRequestModal({ isOpen, onClose, company }) {
  const { mutate, isLoading } = useMutation(mutateFunction);
  const { addToast } = CustomToast();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(0);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);

  const [events, setEvents] = useState([]);
  const [pitchRequests, setPitchRequests] = useState([]);
  const [formData, setFormData] = useState({
    event_id: null,
    deck_url: company?.pitch_deck,
    note: "",
  });

  const SUBSCRIPTION_FLAG = process.env.REACT_APP_SUBSCRIPTION_FLAG;

  const loggedInUserSubscription = useSelector((state) => state?.user?.userSubscriptionInfo);

  const currentPlanName = loggedInUserSubscription?.subscription_plan?.name;


  const statusColors = {
    pending: "yellow",
    shortlisted: "blue",
    invited: "green",
    declined: "red",
  };

  const eligibilityMessages = {
    "phone": "• Please add a valid phone number to your profile.",
    "first_name": "• Please add a valid first name to your profile.",
    "last_name": "• Please add a valid last name to your profile.",
    "video": "Please upload your pitch video in one of these formats: MP4, MOV, AVI, WebM, OGG.",
    "deck": "Please upload your company's pitch deck in PDF format.",
    "fundraising": "It looks like you don't have an active fundraising round yet. Create one to let investors understand what you're raising for and how much you need."
  }

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  // Eligibility check API call
  const {
    data: eligibilityResponse,
    isLoading: isLoadingEligibility,
    refetch: refetchEligibility,
  } = useData(`pitch-requests/check-eligibility?company_id=${company?.id}`, 0, {}, 1, isOpen);

  const {
    data: AllEventsData,
    isLoading: isLoadingAllEvents,
    refetch: refetchEvents,
  } = useData(`events?page_no=1&page_size=20&sort_by=ASC`);

  const {
    data: _pitchRequests,
    isLoading: isLoadingPitchRequests,
    refetch: refetchPitchRequests,
  } = useData(`pitch-requests`);

  // Handle eligibility check when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsCheckingEligibility(true);
      refetchEligibility();
    }
  }, [isOpen, refetchEligibility]);

  useEffect(() => {
    if (eligibilityResponse?.data) {
      setEligibilityData(eligibilityResponse.data);
      setIsCheckingEligibility(false);
    }
  }, [isOpen, eligibilityResponse]);

  useEffect(() => {
    setEvents(AllEventsData?.data?.events || []);
  }, [AllEventsData]);

  useEffect(() => {
    setPitchRequests(_pitchRequests?.data || [])
  }, [_pitchRequests])

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onOpenUpgradePlanModal = () => {
    onClose();
    navigate(`${window.location.pathname}?upgrade=true`, { replace: true });
  }


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.deck_url.trim()) {
      addToast({ message: "Please enter or upload a valid deck URL" }, "warning");
      return;
    }
    const data = {
      company_id: company.id || null,
      deck_url: formData.deck_url.trim(),
      note: formData.note?.trim(),
    }

    if (formData.event_id) {
      data["event_id"] = formData.event_id
    }

    mutate(
      {
        key: "pitch-requests",
        method: "post",
        data,
      },
      {
        onSuccess(res) {
          addToast(res);
          setFormData({ event_id: "", deck_url: "", note: "" });
          refetchPitchRequests()
          // onClose();
        },
        onError(e) {
          addToast({ message: e?.response?.data?.message || "Failed to submit pitch request" }, "error");
          console.error(e);
        },
      }
    );
  };

  const renderEligibilityStatus = () => {
    if (isCheckingEligibility || isLoadingEligibility) {
      return (
        <Box
          bg="gray.50"
          borderRadius="lg"
          p={6}
          border="1px solid"
          borderColor="gray.200"
        >
          <VStack spacing={4}>
            <Spinner size="lg" color="brand.primary" thickness="4px" />
            <Text fontSize="md" fontWeight="medium" color="gray.600">
              Checking your eligibility...
            </Text>
          </VStack>
        </Box>
      );
    }

    if (!eligibilityData) {
      return (
        <Alert status="error" borderRadius="lg" variant="subtle">
          <AlertIcon />
          <Box>
            <AlertTitle>Unable to check eligibility</AlertTitle>
            <AlertDescription>
              There was an error checking your eligibility. Please try again.
            </AlertDescription>
          </Box>
        </Alert>
      );
    }

    const {
      profile,
      deck,
      fundraising,
      video,
      companyDetails,
      allEligible,
      reasons,
    } = eligibilityData;

    if (allEligible) {
      return (
        <Box
          bg="green.50"
          borderRadius="lg"
          p={6}
          border="1px solid"
          borderColor="green.200"
        >
          <HStack spacing={3}>
            <Icon as={CheckIcon} color="green.500" boxSize={6} />
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold" color="green.700">
                You're eligible to pitch!
              </Text>
              <Text fontSize="sm" color="green.600">
                Your profile meets all requirements for pitching at our events.
              </Text>
            </VStack>
          </HStack>
        </Box>
      );
    }

    // Only show what's missing 
    const missingRequirements = [];

    if (!profile) {
      missingRequirements.push({
        icon: FaUser,
        title: "Complete your profile",
        items: reasons.profile || ["Missing required fields"],
        color: "orange"
      });
    }

    if (!deck) {
      missingRequirements.push({
        icon: FaFileAlt,
        title: "Upload a pitch deck",
        items: [reasons.deck || "No pitch deck uploaded"],
        color: "orange"
      });
    }

    if (!fundraising) {
      missingRequirements.push({
        icon: FaDollarSign,
        title: "Active fundraising round",
        items: [reasons.fundraising || "No active fundraising round"],
        color: "orange"
      });
    }

    if (!video) {
      missingRequirements.push({
        icon: FaVideo,
        title: "Pitch video",
        items: [reasons.video || "No pitch video uploaded"],
        color: "orange"
      });
    }

    if (!companyDetails) {
      const companyDetailsReasons = Array.isArray(reasons.companyDetails)
        ? reasons.companyDetails
        : [reasons.companyDetails || "Company information missing required details"];

      missingRequirements.push({
        icon: FaBuilding,
        title: "Complete company details",
        items: companyDetailsReasons,
        color: "orange",
      });
    }

    return (
      <Box
        bg="orange.50"
        borderRadius="lg"
        p={6}
        border="1px solid"
        borderColor="orange.200"
      >
        <VStack spacing={4} align="start">
          <HStack spacing={3}>
            <Icon as={WarningIcon} color="orange.500" boxSize={6} />
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold" color="orange.700">
                Almost there! Let’s get your pitch ready
              </Text>
              <Text fontSize="sm" color="orange.600">
                To continue, please complete the steps below. This helps us ensure your pitch is complete and ready to be reviewed.
              </Text>
            </VStack>
          </HStack>

          <VStack spacing={3} w="full">
            {missingRequirements.map((requirement, index) => (
              <Box
                key={index}
                bg={`${requirement.color}.50`}
                borderRadius="md"
                p={4}
                w="full"
                border="1px solid"
                borderColor={`${requirement.color}.200`}
              >
                <HStack spacing={3} align="start">
                  <Icon
                    as={requirement.icon}
                    color={`${requirement.color}.600`}
                    boxSize={5}
                    mt={0.5}
                  />
                  <VStack align="start" spacing={2} flex={1}>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={`${requirement.color}.700`}
                    >
                      {requirement.title}
                    </Text>
                    <VStack align="start" spacing={1}>
                      {requirement.items.map((item, itemIndex) => (
                        <>
                        <Text
                          key={itemIndex}
                          fontSize="xs"
                          color={`${requirement.color}.600`}
                          bg={`${requirement.color}.50`}
                          px={2}
                          py={1}
                          borderRadius="sm"
                        >
                          {
                            item.includes("video") 
                              ? eligibilityMessages["video"] : item.includes("phone") 
                              ? eligibilityMessages["phone"] : item.includes("fundraising")
                              ? eligibilityMessages["fundraising"] : item.includes("deck")
                              ? eligibilityMessages["deck"] : item.includes("first_name") 
                              ? eligibilityMessages["first_name"] : item.includes("last_name") 
                              ? eligibilityMessages["last_name"] : `• ${item}`
                            }
                        </Text>
                        {
                          item.includes("deck") && (
                            <BrandButton
                                width="fit-content"
                                text="Set up your fundraising round"
                                bg={"brand.primary"}
                                color="#fff"
                                fontSize="14px"
                                borderRadius={4}
                                borderColor={"brand.primary"}
                                px={5}
                                _hover={{ background: "brand.primary" }}
                                onClick={() => {
                                  onClose(); 
                                  navigate('/deal-flow');
                                }}
                          />
                          )
                        }
                        </>
                      ))}
                    </VStack>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </VStack>

          <Box
            bg="blue.50"
            borderRadius="md"
            p={3}
            w="full"
            border="1px solid"
            borderColor="blue.200"
          >
            <Text fontSize="sm" color="blue.700" fontWeight="medium">
              💡 Need help? Visit your{" "}
              <Text
                as="span"
                color="blue.600"
                textDecoration="underline"
                cursor="pointer"
                _hover={{ color: "blue.800" }}
                onClick={() => {
                  onClose(); // Close the modal first
                  navigate('/account-settings');
                }}
              >
                profile settings
              </Text>{" "}
              to update your information and upload required documents.
            </Text>
          </Box>


        </VStack>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent position="relative">
        {/* Close Icon with Tooltip */}
        <Tooltip label="Close" placement="bottom" hasArrow>
          <Box
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
            cursor="pointer"
            onClick={onClose}
            _hover={{ transform: "scale(1.1)" }}
            transition="all 0.2s"
          >
            <Icon as={CloseIcon} boxSize={4} color="gray.500" />
          </Box>
        </Tooltip>

        <ModalBody p={4}>
          <Flex flexDirection="column">
            <Stack spacing={6} mb={2}>
              <Flex justify="center" align="center" gap={2}>
                <Text fontSize={20} fontWeight={700} color="brand.primary" align="center">
                  Pitch Request
                </Text>
                <AiOutlineFileAdd style={{ color: "rgba(0,0,0,0.7)", fontSize: "26px" }} />
              </Flex>

            </Stack>

            {/* Enhanced Eligibility Status */}
            {renderEligibilityStatus()}

            {eligibilityData?.allEligible && (

              <>
                <Tabs
                  className="w-full pt-5"
                  index={tabIndex}
                  onChange={handleTabsChange}
                >
                  <TabList justifyContent={"center"}>
                    <Tab>Create a Request</Tab>
                    <Tab>View Requests</Tab>
                  </TabList>
                </Tabs>

                {
                  tabIndex == 0 && (
                    <>
                      <form onSubmit={handleSubmit}>
                        <Stack spacing={4} mt={6}>
                          <Text fontSize={14} color="#000000" textAlign="left">
                            SeedAFounder organises events to bring investors and founders together. Fill the form to indicate interest in pitching at one of our physical or virtual events.
                          </Text>
                          {/* Deck URL */}
                          <Flex flexDirection="column" gap={2}>
                            <Text fontSize={14} fontWeight={500}>Pitch Deck URL *</Text>
                            <Input
                              placeholder="https://example.com/pitch-deck.pdf"
                              name="deck_url"
                              value={formData.deck_url}
                              onChange={handleChange}
                              isRequired
                            />
                          </Flex>

                          {/* Optional Event Dropdown */}
                          {/* <Flex flexDirection="column" gap={2}>
                            <Text fontSize={14} fontWeight={500}>Event (Optional)</Text>
                            <Select
                              placeholder="Select an event"
                              name="event_id"
                              value={formData.event_id}
                              onChange={handleChange}
                            >
                              {events.map((event) => (
                                <option key={event.id} value={event.id}>
                                  {event.name}
                                </option>
                              ))}
                            </Select>
                          </Flex> */}

                          {/* Note */}
                          <Flex flexDirection="column" gap={2}>
                            <Text fontSize={14} fontWeight={500}>Additional Note</Text>
                            <Textarea
                              placeholder="Any extra information you'd like to share..."
                              name="note"
                              value={formData.note}
                              onChange={handleChange}
                              maxLength={500}
                              rows={5}
                            />
                          </Flex>
                        </Stack>


                        {SUBSCRIPTION_FLAG == "on" ? (
                          currentPlanName?.toLowerCase().includes('pro') ?
                            <Flex gap={6} mt={8} justify="center">
                              <BrandButton
                                isLoading={isLoading}
                                text="Submit"
                                type="submit"
                                onClick={handleSubmit}
                                bg={formData?.deck_url?.trim() ? "brand.primary" : "grey"}
                                color="#fff"
                                fontSize="14px"
                                borderRadius={4}
                                borderColor={formData?.deck_url?.trim() ? "brand.primary" : "grey"}
                                px={5}
                                _hover={{ background: formData?.deck_url?.trim() ? "brand.primary" : "grey" }}
                              />
                              <BrandButton text="Close" onClick={onClose} />
                            </Flex> :
                            <Flex mt={8} justify="center">
                              <Button
                                w={{ base: "full", md: "50%", lg: "40%" }}
                                bg="brand.primary"
                                color="#fff"
                                fontSize="14px"
                                fontWeight={500}
                                borderRadius={4}
                                borderColor="brand.primary"
                                px={6}
                                py={6}
                                onClick={() => onOpenUpgradePlanModal()}
                                _hover={{
                                  bg: "brand.primary",
                                  opacity: 0.9
                                }}
                                _active={{
                                  bg: "brand.primary",
                                  opacity: 0.8
                                }}
                              >
                                Upgrade to Pro Plan
                              </Button>
                            </Flex>
                        ) : (
                          <Flex gap={6} mt={8} justify="center">
                            <BrandButton
                              isLoading={isLoading}
                              text="Submit"
                              type="submit"
                              onClick={handleSubmit}
                              bg={formData?.deck_url?.trim() ? "brand.primary" : "grey"}
                              color="#fff"
                              fontSize="14px"
                              borderRadius={4}
                              borderColor={formData?.deck_url?.trim() ? "brand.primary" : "grey"}
                              px={5}
                              _hover={{ background: formData?.deck_url?.trim() ? "brand.primary" : "grey" }}
                            />
                            <BrandButton text="Close" onClick={onClose} />
                          </Flex>
                        )}

                      </form>
                    </>
                  )
                }

                {
                  tabIndex == 1 && (
                    <Flex
                      direction="column"
                      gap="16px"
                      className="mt-4 w-full max-w-5xl mx-auto"
                    >
                      <Text fontSize={14} color="#000000" textAlign="left">
                        See the status of requested pitch slots.
                      </Text>
                      {pitchRequests?.length > 0 ? pitchRequests.map((request) => (
                        <Box
                          key={request.id}
                          className="rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all bg-white p-5"
                        >
                          {/* Header */}
                          <Flex justify="space-between" align="center" className="mb-3">
                            <Text className="font-semibold text-lg text-gray-800">
                              {request.company?.title || "Unnamed Company"}
                            </Text>
                            <Badge
                              colorScheme={statusColors[request.status] || "gray"}
                              className="px-2 py-1 rounded-md text-sm capitalize"
                            >
                              {request.status}
                            </Badge>
                          </Flex>

                          {/* Body */}
                          <Flex direction="column" className="space-y-2 text-sm text-gray-700">
                            {/* Event */}
                            <Flex justify="space-between">
                              <Text className="font-medium text-gray-600">Event:</Text>
                              <Text className="text-gray-700">
                                {request.event?.name || <span className="text-gray-400">None</span>}
                              </Text>
                            </Flex>

                            {/* Deck */}
                            {request.deck_url && (
                              <Flex justify="space-between" align="center">
                                <Text className="font-medium text-gray-600">Deck:</Text>
                                <Text
                                  color="brand.primary"
                                  className="flex items-center text-blue-600 hover:underline"
                                >
                                  View Deck <a href={request.deck_url} target="_blank" ><ExternalLinkIcon ml={1} /></a>
                                </Text>
                              </Flex>
                            )}

                            {/* Notes */}
                            {request.notes && (
                              <Box>
                                <Text className="font-medium text-gray-600 mb-1">Notes:</Text>
                                <Text className="text-gray-700">
                                  {request.notes.length > 100
                                    ? request.notes.slice(0, 100) + "..."
                                    : request.notes}
                                </Text>
                              </Box>
                            )}

                            {/* Footer / Timestamps */}
                            <Flex justify="space-between" className="pt-2 mt-2 border-t border-gray-100 text-xs text-gray-500">
                              <Text>Submitted</Text>
                              <Text>{dayjs(request.createdAt).format("MMM D, YYYY")}</Text>
                            </Flex>
                          </Flex>
                        </Box>
                      )) : (

                        <Box className="py-20 px-5">
                          <Text className="font-medium text-gray-600 text-sm text-center">You are yet to request to pitch at an event</Text>
                        </Box>
                      )}
                    </Flex>)
                }
              </>
            )}

          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}